import { create } from "zustand";
import type { Params, PlanKind, PlanObject, PresetId, Template } from "@mcp/plan";

export type LayoutState =
  | { mode: "fill"; targetId: string; preset: PresetId; params: Params; scale: number }
  | { mode: "place"; templateId: string; turns: number; scale: number; mirror: boolean; scaleSizes: boolean };
import type { DimensionInfo, LevelInfo } from "@mcp/world";
import type { EngineStats } from "../engine/Engine.ts";
import type { Invite, WorldManifest } from "../lib/share.ts";
import { applySupporterParam, isSupporter } from "../lib/entitlements.ts";
import { isDesktop, PUBLIC_WEB_URL, RETIRED_SERVERS } from "../lib/desktop.ts";
import type { SchematicEntry } from "../lib/schematics.ts";
import type { InvitePreview, PlanLimits, Role } from "../lib/collab.ts";

/**
 * The shared world the open plan is live in (paywall plan P2).
 * account: owned by an account, entered with tickets · legacy: a room from before accounts (host key / open link)
 */
export interface RoomState {
  id: string;
  kind: "account" | "legacy";
  role: Role;
  /** the owner's plan doesn't cover this world: everyone can open it, nobody edits */
  paused: boolean;
  name: string;
  ownerName: string | null;
  plan: PlanLimits | null;
}

applySupporterParam();

export type Tool = "select" | PlanKind;

/**
 * The MC Planner server. The website is served by the same Cloudflare Worker as the collaboration backend, so on
 * the web the page's own origin is the server (apps/web/.env.production: VITE_COLLAB_URL=same-origin). The desktop
 * app talks to the public website; `pnpm dev` to the local backend (`pnpm collab`). A full URL in VITE_COLLAB_URL
 * (e.g. a self-hosted server) wins. Users can still override it under Collaborate → Advanced.
 */
function defaultServer() {
  const env = (import.meta.env?.VITE_COLLAB_URL as string | undefined)?.trim();
  if (env && env !== "same-origin") return env;
  if (isDesktop()) return new URL(PUBLIC_WEB_URL).origin;
  if (env === "same-origin" && /^https?:$/.test(location.protocol)) return location.origin;
  return "http://localhost:8787";
}
export const DEFAULT_SERVER = defaultServer();

/** the server saved under Collaborate → Advanced, unless it is a retired public address (lib/desktop.ts) */
function savedServer(): string | undefined {
  const s = (saved.collab as { server?: string } | undefined)?.server?.trim();
  if (!s) return undefined;
  const host = (u: string) => { try { return new URL(/^[a-z]+:\/\//i.test(u) ? u : `https://${u}`).host; } catch { return u; } };
  return RETIRED_SERVERS.some((r) => host(r) === host(s)) ? undefined : s;
}
export type Phase = "setup" | "loading" | "ready";
/** what "Share world" is doing: getting ready · waiting for the map · uploading files · publishing the manifest */
export type ShareStep = "prepare" | "map" | "upload" | "publish";

export interface CollabState {
  /** idle = live, but paused while nobody is using this tab (reconnects on its own — PlanSession) */
  status: "offline" | "connecting" | "connected" | "error" | "ended" | "idle";
  url: string;
  name: string;
  /** who else is live right now (uid: their account, for worlds of accounts) */
  peers: { name: string; color: string; uid?: string }[];
}

export interface AppState {
  phase: Phase;
  progress: { msg: string; frac: number } | null;
  error: string | null;
  /** the troubleshooting anchor for `error` (ui/Academy.tsx); shown only while `error` is still this message */
  errorHelp: { key: "worldWontOpen" | "noTextures"; message: string } | null;
  assetsLabel: string | null;
  /** guest without a game jar: region-map colours only, no 3D blocks (lib/mapColours.ts) */
  mapColoursOnly: boolean;
  /** counts the server's "room changed" notices (members joined/left/changed, files uploaded): open panels refresh */
  roomRev: number;
  /** the demo island is open ("Try the demo world": generated terrain, plan stays in this browser) */
  demo: boolean;
  level: LevelInfo | null;
  worldId: string | null;
  dimensions: DimensionInfo[];
  dimension: string;
  tool: Tool;
  /** primary selection (inspector, handles) */
  selectedId: string | null;
  /** all selected objects (Shift/Ctrl+click adds); always contains selectedId */
  selectedIds: string[];
  /** shared custom colour palette of this world + per-kind default colours */
  palette: string[];
  /** recently used block materials of this world (shared) */
  recentMaterials: string[];
  kindColors: Partial<Record<PlanKind, string>>;
  /** home location of the current dimension (shared) */
  home: { x: number; y: number; z: number; setBy?: string } | null;
  plans: PlanObject[];
  hover: { x: number; y: number; z: number } | null;
  draftInfo: string | null;
  renderDistance: number;
  cutEnabled: boolean;
  cutY: number;
  topDown: boolean;
  showLabels: boolean;
  /** outline lines of plan objects */
  showLines: boolean;
  stats: EngineStats | null;
  collab: CollabState;
  /** a world to join: from a #join link (legacy) or an accepted #invite link / "your shared worlds" (account) */
  invite: (Invite & { manifest: WorldManifest | null; error?: string; account?: { role: Role; ownerName: string | null; paused: boolean } }) | null;
  /** a #invite=… link that was opened (account worlds): shown before signing in / joining */
  accountInvite: { token: string; server: string; preview: InvitePreview | null; error?: string; busy?: boolean } | null;
  /** the shared world the open plan belongs to (null = only in this browser) */
  room: RoomState | null;
  /** view only: viewer role or a paused world (PlanSession.readOnly) */
  readOnly: boolean;
  /** world is streamed from the collab server (we are a guest) */
  guestOf: string | null;
  /** "Share world": while uploading, `step` + `frac` (0…1, or null while unknown) drive the cloud animation (ui/CloudUpload.tsx) */
  share: { state: "idle" | "uploading" | "shared" | "error"; msg?: string; step?: ShareStep; frac?: number | null; manifest?: WorldManifest | null };
  /** where the plan is saved right now (see PlanSession.SaveState) */
  saveState: "local" | "syncing" | "synced" | "offline";
  /** plan versions of this world (newest first, without their objects) */
  versions: { id: string; name: string; author: string; createdAt: number; count?: number }[];
  /** help overlay (? / F1) */
  helpOpen: boolean;
  /** day/night: Minecraft light levels on/off, time in ticks (0 sunrise · 6000 noon · 12000 sunset · 18000 night) */
  lighting: { on: boolean; time: number; playing: boolean; speed: number };
  /** pinboard: object whose board is open, a counter that changes whenever any card changes, cards whose link preview is loading */
  boardFor: string | null;
  boardTick: number;
  unfurling: string[];
  /** layouts & templates: which panel tab is open, what's being previewed, template libraries */
  layoutPanel: "fill" | "templates" | "schematics" | null;
  layout: LayoutState | null;
  worldTemplates: Template[];
  myTemplates: Template[];
  /** my imported schematic files (this browser, all worlds — lib/schematics.ts) */
  schematics: SchematicEntry[];
  /** schematic being placed on the map (follows the mouse) */
  schemPlace: { hash: string; turns: number; mirror: boolean } | null;
  /** just imported: show its name / source-link form */
  schemEdit: string | null;
  /** the full-window Schematics view (open while layoutPanel === "schematics"): selected file hash */
  schemSelected: string | null;
  /** reading a schematic file / building a 3D preview */
  schemImporting: boolean;
  schemMeshing: boolean;
  /** supporter: no ads (lib/entitlements.ts) */
  supporter: boolean;
  /** desktop: Minecraft saved the open world since we read it */
  worldChanged: boolean;
  /** short message with an optional file to show in the file manager */
  notice: { text: string; path?: string } | null;
  /** the plan list on the left is folded into a narrow rail (Ctrl B; remembered) */
  sideCollapsed: boolean;
  /** the "Plan together" sheet on the right of the map is open */
  collabOpen: boolean;
  set: (p: Partial<AppState>) => void;
}

const saved = (() => {
  try {
    return JSON.parse(localStorage.getItem("mcp-settings") ?? "{}") as Partial<AppState>;
  } catch {
    return {};
  }
})();

export const useApp = create<AppState>((set) => ({
  phase: "setup",
  progress: null,
  error: null,
  errorHelp: null,
  assetsLabel: null,
  mapColoursOnly: false,
  roomRev: 0,
  demo: false,
  level: null,
  worldId: null,
  dimensions: [],
  dimension: "minecraft:overworld",
  tool: "select",
  selectedId: null,
  selectedIds: [],
  palette: [],
  recentMaterials: [],
  kindColors: {},
  home: null,
  plans: [],
  hover: null,
  draftInfo: null,
  renderDistance: saved.renderDistance ?? 10,
  cutEnabled: false,
  cutY: 64,
  topDown: false,
  showLabels: saved.showLabels ?? true,
  showLines: (saved as { showLines?: boolean }).showLines ?? true,
  stats: null,
  // only a deliberate custom server is remembered (older saved URLs, e.g. Tailscale, are ignored)
  collab: { status: "offline", url: savedServer() || DEFAULT_SERVER, name: (saved.collab as CollabState | undefined)?.name ?? "", peers: [] },
  invite: null,
  accountInvite: null,
  room: null,
  readOnly: false,
  guestOf: null,
  share: { state: "idle" },
  saveState: "local",
  versions: [],
  helpOpen: false,
  supporter: isSupporter(),
  layoutPanel: null,
  layout: null,
  worldTemplates: [],
  schematics: [],
  schemPlace: null,
  schemEdit: null,
  schemSelected: null,
  schemImporting: false,
  schemMeshing: false,
  myTemplates: (() => { try { return JSON.parse(localStorage.getItem("mcp-templates") ?? "[]") as Template[]; } catch { return []; } })(),
  boardFor: null,
  boardTick: 0,
  unfurling: [],
  lighting: { on: false, time: 6000, playing: false, speed: 200, ...((saved as { lighting?: object }).lighting ?? {}) },
  worldChanged: false,
  notice: null,
  sideCollapsed: (saved as { sideCollapsed?: boolean }).sideCollapsed ?? false,
  collabOpen: false,
  set: (p) => set(p),
}));

/** Select objects; the last one becomes the primary selection. */
export function select(ids: string[]) {
  const uniq = [...new Set(ids)];
  useApp.getState().set({ selectedIds: uniq, selectedId: uniq[uniq.length - 1] ?? null });
}

// keep selectedIds consistent when code sets selectedId directly
useApp.subscribe((s, prev) => {
  if (s.selectedId === prev.selectedId) return;
  const ok = s.selectedId ? s.selectedIds.includes(s.selectedId) : s.selectedIds.length === 0;
  if (!ok) s.set({ selectedIds: s.selectedId ? [s.selectedId] : [] });
});

useApp.subscribe((s) => {
  try {
    localStorage.setItem("mcp-settings", JSON.stringify({ renderDistance: s.renderDistance, showLabels: s.showLabels, showLines: s.showLines, sideCollapsed: s.sideCollapsed, lighting: { on: s.lighting.on, time: Math.round(s.lighting.time), speed: s.lighting.speed }, collab: { server: s.collab.url !== DEFAULT_SERVER && !s.guestOf ? s.collab.url : undefined, name: s.collab.name } }));
  } catch { /* ignore */ }
});
